import React from "react";
import { CurrentPlan } from "../model/PricingModel";
interface CurrentPlanSectionProps {
  currentPlanClick?: () => void;
  currentPlan: CurrentPlan | null;
  handleManageBilling: () => void;
}
const CurrentPlanSection: React.FC<CurrentPlanSectionProps> = ({
  currentPlanClick,
  currentPlan,
  handleManageBilling,
}) => {

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };
  const formatPrice = (price?: number) => {
    if (typeof price !== "number") {
      return null;
    }
    return `$${Number.isInteger(price) ? price.toFixed(0) : price.toFixed(2)}`;
  };

  if (!currentPlan) return null;

  const subscriptionMessage = currentPlan.subscription_status_msg?.trim();
  const subscriptionTone = /cancel|past due|expired/i.test(subscriptionMessage || "")
    ? "danger"
    : /update|changed|change|switch|downgrade|upgrade/i.test(subscriptionMessage || "")
      ? "warning"
      : "info";
  const subscriptionMessageClasses = {
    danger: "border-red-200 bg-red-50 text-red-800",
    warning: "border-amber-200 bg-amber-50 text-amber-800",
    info: "border-blue-200 bg-blue-50 text-blue-800",
  }[subscriptionTone];

  return (
    <div>
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            Your Current Plan
          </h2>
        </div>
        {/* Main Card */}
        <div className="border border-gray-200 rounded-2xl p-6">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left Section */}
            <div className="flex-1">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shadow-md">
                  <span className="text-white text-3xl font-bold">⚡</span>
                </div>

                <div className="w-full">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                            currentPlan.subscription_status === "PAST_DUE"
                              ? "bg-red-100 text-red-700"
                              : "bg-green-100 text-green-700"
                          }`}
                        >
                          {currentPlan.subscription_status}
                        </span>
                      </div>

                      {subscriptionMessage && (
                        <div
                          className={`mt-3 rounded-xl border-l-4 px-4 py-3 ${subscriptionMessageClasses}`}
                        >
                          <p className="text-sm leading-6 font-medium">
                            {subscriptionMessage}
                          </p>
                        </div>
                      )}

                      <h3 className="text-3xl font-bold text-gray-900 truncate">
                        {currentPlan.name}
                      </h3>
                    </div>

                    <div className="grid grid-cols-2 gap-4 min-w-[220px]">
                      <div className="rounded-2xl bg-gray-50 p-3">
                        <p className="text-xs text-gray-500">Billing Period</p>
                        <p className="font-semibold text-gray-900">
                          {currentPlan.billing_period}
                        </p>
                      </div>

                      <div className="rounded-2xl bg-gray-50 p-3">
                        <p className="text-xs text-gray-500">Locked Price</p>
                        <p className="font-semibold text-gray-900">
                          {formatPrice(currentPlan.locked_price)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8 items-center">
                {/* Start Billing Date */}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    📅
                  </div>

                  <div>
                    <p className="text-xs text-gray-500">Start billing date</p>

                    <p className="font-semibold text-gray-900">
                      {formatDate(currentPlan.current_period_start)}
                    </p>
                  </div>
                </div>

                {/* Next Billing Date */}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    📅
                  </div>

                  <div>
                    <p className="text-xs text-gray-500">Next billing date</p>

                    <p className="font-semibold text-gray-900">
                      {formatDate(currentPlan.current_period_end)}
                    </p>
                  </div>
                </div>

                {/* Current Plan Button */}
                <div className="flex justify-start md:justify-end">
                  <button
                    onClick={handleManageBilling}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-xl transition-all"
                  >
                    💳 Manage Billing →
                  </button>
                </div>
              </div>
            </div>

            {/* Right Section */}
            <div className="lg:w-[320px] border-l border-gray-200 pl-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-5">
                What's included in your plan
              </h4>

              <div className="space-y-4">
                {currentPlan.features.map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center text-green-600 text-xs">
                      ✓
                    </div>

                    <span className="text-gray-700 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default CurrentPlanSection;
